import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import StatusMessage from './StatusMessage';
import outputs from '../amplify_outputs.json';
import { 
  trackCourseRegistration, 
  trackFormError, 
  trackSpamDetection,
  trackFormStep,
  trackFormButton,
  trackStepTime,
  trackFormAbandonment
} from '../utils/matomoEvents';

import './CourseRegistrationForm.css';

interface Course {
  id: string;
  title: string;
  description: string;
  price: string;
  instructor: string;
  duration: string;
  level: string;
  location?: string;
  startDate?: string;
  endDate?: string;
  targetgroup?: string;
  isActive: boolean;
  environment: string;
}

interface Participant {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface Registrant {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface RegistrationData {
  courseId: string;
  participants: Participant[];
  registrant: Registrant;
  company: string;
  website: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  notes: string;
  specialRequirements: string;
  knownFrom: string;
  // Honeypot fields - hidden from users, bots will fill them
  hp_website?: string;
  hp_confirm_email?: string;
  // Security metadata
  formStartTime?: number;
}

type CourseRegistrationFormProps = {
  preselectedCourseId?: string;
  preselectedCourseTitle?: string;
  skipSelection?: boolean;
};

const CourseRegistrationForm: React.FC<CourseRegistrationFormProps> = ({ preselectedCourseId, preselectedCourseTitle, skipSelection }) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [registrationType, setRegistrationType] = useState<'self' | 'other'>('self');
  const [participantCount, setParticipantCount] = useState<number>(1);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
  const [stepStartTime, setStepStartTime] = useState<number>(Date.now());
  
  const [formData, setFormData] = useState<RegistrationData>({
    courseId: '',
    participants: [{ firstName: '', lastName: '', email: '', phone: '' }],
    registrant: { firstName: '', lastName: '', email: '', phone: '' },
    company: '',
    website: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    notes: '',
    specialRequirements: '',
    knownFrom: '',
    // Anti-spam defaults
    hp_website: '',
    hp_confirm_email: '',
    formStartTime: Date.now(),
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  // Update participants array when participant count changes (only for 'other' registration type)
  useEffect(() => {
    if (registrationType === 'self') {
      // For self-registration, always keep exactly 1 participant
      setFormData(prev => ({
        ...prev,
        participants: prev.participants.length > 0 ? [prev.participants[0]] : [{ firstName: '', lastName: '', email: '', phone: '' }]
      }));
    } else {
      // For registering others, create array based on participant count
      // BUG FIX: Use prev.participants instead of stale formData.participants
      setFormData(prev => {
      const newParticipants = Array.from({ length: participantCount }, (_, index) => 
          prev.participants[index] || { firstName: '', lastName: '', email: '', phone: '' }
      );
        return { ...prev, participants: newParticipants };
      });
    }
  }, [participantCount, registrationType]);

  // Preselect course when provided (used on course detail page)
  useEffect(() => {
    if (!skipSelection) return;
    if (!preselectedCourseId) return;
    // Try to find in loaded courses
    const found = (courses || []).find(c => c.id === preselectedCourseId) || null;
    if (found) {
      setSelectedCourse(found);
      setFormData(prev => ({ ...prev, courseId: found.id }));
      return;
    }
    // Otherwise set a minimal placeholder so submit works and title is shown
    const placeholder: Course = {
      id: preselectedCourseId,
      title: preselectedCourseTitle || 'Ausgewählter Kurs',
      description: '',
      price: '',
      instructor: '',
      duration: '',
      level: '',
      environment: '',
      isActive: true,
    };
    setSelectedCourse(placeholder);
    setFormData(prev => ({ ...prev, courseId: preselectedCourseId }));
  }, [skipSelection, preselectedCourseId, preselectedCourseTitle, courses]);

  // Initialize step based on skipSelection
  useEffect(() => {
    if (skipSelection && preselectedCourseId) {
      // Skip course selection step when course is preselected
      setCurrentStep(1);
    }
  }, [skipSelection, preselectedCourseId]);

  // Track step entry when currentStep changes
  useEffect(() => {
    if (!showSuccessScreen) {
      trackFormStep('Course Registration', currentStep, getStepName(currentStep), 'entered');
      setStepStartTime(Date.now()); // Reset timer when entering new step
    }
  }, [currentStep, showSuccessScreen]);

  // Track form abandonment when component unmounts
  useEffect(() => {
    return () => {
      // Only track abandonment if form wasn't successfully submitted
      if (!showSuccessScreen && currentStep > 1) {
        trackFormAbandonment('Course Registration', currentStep, currentStep);
      }
    };
  }, [showSuccessScreen, currentStep]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const client = generateClient<Schema>({ authMode: 'iam' });

      // Fetch courses - Amplify handles environment isolation automatically

      const { data, errors } = await client.models.Course.list({
        filter: {
          isActive: { eq: true }
        }
      });

      if (errors && errors.length > 0) {
        console.error('🔴 Course query errors:', JSON.stringify(errors, null, 2));
        setMessage({ type: 'error', text: 'Fehler beim Laden der Kurse. Bitte versuchen Sie es später erneut.' });
      } else {
        setCourses((data || []) as Course[]);
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Kurse. Bitte versuchen Sie es später erneut.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCourseSelect = (course: Course) => {
    setSelectedCourse(course);
    setFormData(prev => ({ ...prev, courseId: course.id }));
    setMessage(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Special handling for postal codes - only allow 5 digits
    if (name === 'postalCode') {
      const numericValue = value.replace(/\D/g, '').slice(0, 5);
      setFormData(prev => ({ ...prev, [name]: numericValue }));
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleParticipantChange = (index: number, field: keyof Participant, value: string) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.map((participant, i) => 
        i === index ? { ...participant, [field]: value } : participant
      )
    }));
  };

  const handleRegistrantChange = (field: keyof Registrant, value: string) => {
    setFormData(prev => ({
      ...prev,
      registrant: { ...prev.registrant, [field]: value }
    }));
  };

  const validateAllRequiredFields = (): boolean => {
    // Validate course selection
    if (!selectedCourse) {
      setMessage({ type: 'error', text: 'Bitte wählen Sie einen Kurs aus.' });
      return false;
    }

    // Validate all participants using the same logic as validateParticipants
    return validateParticipants();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Track submit button click
    trackFormButton('Course Registration', 'Submit', currentStep);
    trackStepTime('Course Registration', currentStep, Math.floor((Date.now() - stepStartTime) / 1000));
    
    // Validate all required fields before submission
    if (!validateAllRequiredFields()) {
      return;
    }

    // Client-side spam checks
    const now = Date.now();
    const started = formData.formStartTime || now;
    const elapsedMs = now - started;

    // 1) Honeypot check
    if ((formData.hp_website && formData.hp_website.trim() !== '') || (formData.hp_confirm_email && formData.hp_confirm_email.trim() !== '')) {
      trackSpamDetection('Course Registration', 'Honeypot triggered');
      setMessage({ type: 'error', text: 'Ihre Anmeldung konnte nicht verarbeitet werden. Bitte kontaktieren Sie uns direkt.' });
      return;
    }

    // 2) Timing check: must spend at least 5 seconds on the form
    if (elapsedMs < 5000) {
      trackSpamDetection('Course Registration', 'Form submitted too fast');
      setMessage({ type: 'error', text: 'Bitte nehmen Sie sich einen Moment Zeit, das Formular vollständig auszufüllen.' });
      return;
    }

    // 3) Simple local rate limit: max 3 submissions per 10 minutes per browser
    try {
      const key = 'course_reg_rate';
      const itemRaw = localStorage.getItem(key);
      const windowMs = 10 * 60 * 1000;
      const maxCount = 3;
      const nowTs = Date.now();
      let record = { ts: nowTs, count: 0 } as { ts: number; count: number };
      if (itemRaw) {
        try {
          const parsed = JSON.parse(itemRaw) as { ts: number; count: number };
          if (parsed && typeof parsed.ts === 'number' && typeof parsed.count === 'number') {
            if (nowTs - parsed.ts < windowMs) {
              record = parsed;
            }
          }
        } catch {}
      }
      if (record.count >= maxCount && nowTs - record.ts < windowMs) {
        trackSpamDetection('Course Registration', 'Rate limit exceeded');
        setMessage({ type: 'error', text: 'Zu viele Anfragen. Bitte versuchen Sie es später erneut.' });
        return;
      }
      if (nowTs - record.ts >= windowMs) {
        record = { ts: nowTs, count: 0 };
      }
      record.count += 1;
      localStorage.setItem(key, JSON.stringify(record));
    } catch {}

    setSubmitting(true);
    setMessage(null);

    try {
      const client = generateClient<Schema>({ authMode: 'iam' });
      
      // CRITICAL BUG FIX: Ensure self-registration only creates 1 participant
      // This prevents duplicate registrations from stale state or race conditions
      if (registrationType === 'self' && formData.participants.length !== 1) {
        console.error('CRITICAL BUG DETECTED: Self-registration has ' + formData.participants.length + ' participants instead of 1!');
        console.error('Participants data:', formData.participants);
        // Force exactly 1 participant for self-registration
        formData.participants = [formData.participants[0]];
        console.log('Fixed: Reduced to 1 participant for self-registration');
      }
      
      // Additional validation: Log the exact number of registrations being created
      console.log(`Creating ${formData.participants.length} registration(s) for ${registrationType} registration`);
      console.log('Participants array:', formData.participants);
      
      // CRITICAL: Generate a single timestamp for ALL participants in this submission
      // This ensures all participants from the same form submission are grouped together
      // in the admin invoices page, while keeping separate submissions distinct
      const submissionTimestamp = new Date().toISOString();
      console.log('Submission timestamp for grouping:', submissionTimestamp);
      
      // Create separate Registration records for each participant
      // Each person gets their own registration record with shared organization/billing info
      
      const registrationPromises = formData.participants.map(async (participant, index) => {
        const registrationData = {
          courseId: formData.courseId,
          // Participant information (the person being registered)
          firstName: participant.firstName,
          lastName: participant.lastName,
          email: participant.email,
          phone: participant.phone,
          // Registrant information (person who made the registration)
          registrantFirstName: registrationType === 'self' ? participant.firstName : formData.registrant.firstName,
          registrantLastName: registrationType === 'self' ? participant.lastName : formData.registrant.lastName,
          registrantEmail: registrationType === 'self' ? participant.email : formData.registrant.email,
          registrantPhone: registrationType === 'self' ? participant.phone : formData.registrant.phone,
          // Organization and address details (shared across all registrations)
          company: formData.company,
          website: formData.website,
          street: formData.street,
          houseNumber: formData.houseNumber,
          postalCode: formData.postalCode,
          city: formData.city,
          // Additional information (shared across all registrations)
          notes: formData.notes,
          specialRequirements: formData.specialRequirements,
          // Note: knownFrom field temporarily disabled until database schema is updated
          // ...(formData.knownFrom && formData.knownFrom.trim() && { knownFrom: formData.knownFrom.trim() }),
          // Metadata
          registrationDate: submissionTimestamp, // Use shared timestamp for proper grouping
          status: 'pending',
          paymentStatus: 'pending',
          isActive: true,
          confirmationEmailSent: false,
          reminderEmailSent: false
        };

        console.log(`Creating registration ${index + 1} for participant:`, participant.firstName, participant.lastName);
        console.log('Registration data being sent:', JSON.stringify(registrationData, null, 2));
        console.log('KnownFrom value:', formData.knownFrom);
        console.log('KnownFrom included in data:', !!(registrationData as any).knownFrom);
        
        return client.models.Registration.create(registrationData);
      });

      // Wait for all registration records to be created
      const registrationResults = await Promise.all(registrationPromises);
      console.log('All registrations created:', registrationResults);

      // Check if any registration creation failed
      const failedRegistrations = registrationResults.filter(result => !result.data?.id);
      if (failedRegistrations.length > 0) {
        console.error('Some registration records failed to create:', failedRegistrations);
        // Log detailed error information
        failedRegistrations.forEach((result, index) => {
          console.error(`Registration ${index + 1} failed:`, result.errors);
          if (result.errors && result.errors.length > 0) {
            result.errors.forEach((error: any, errorIndex: number) => {
              console.error(`Error ${errorIndex + 1}:`, error);
              if (error.message) {
                console.error(`Error message:`, error.message);
              }
              if (error.errorType) {
                console.error(`Error type:`, error.errorType);
              }
            });
          }
        });
        throw new Error(`Failed to create ${failedRegistrations.length} registration records`);
      }

      // Send ONE email notification per registration event (not per participant)
      const successfulRegistrations = registrationResults.filter(result => result.data?.id);
      console.log('Sending ONE email notification for entire registration group:', successfulRegistrations.length, 'participants');
      
      // Get function URL for course registration emails
      const functionUrl = (outputs as any)?.custom?.COURSE_REGISTRATION_URL || process.env.REACT_APP_COURSE_REGISTRATION_URL || '';
      
      if (functionUrl && successfulRegistrations.length > 0) {
        // Collect all registration IDs
        const registrationIds = successfulRegistrations.map(result => result.data!.id);
        
        // Get the first participant as the primary contact (or registrant if 'others')
        const primaryParticipant = formData.participants[0];
        
        // Build email payload with ALL participants
          const emailPayload = {
          registrationIds: registrationIds, // Array of all IDs
            courseId: formData.courseId,
          courseTitle: selectedCourse?.location 
            ? `${selectedCourse.title} ${selectedCourse.location}` 
            : selectedCourse?.title || 'Unbekannter Kurs',
          // Primary participant (for email subject line)
          firstName: primaryParticipant.firstName,
          lastName: primaryParticipant.lastName,
          email: primaryParticipant.email,
          phone: primaryParticipant.phone,
          // Registrant information
          registrantFirstName: registrationType === 'self' ? primaryParticipant.firstName : formData.registrant.firstName,
          registrantLastName: registrationType === 'self' ? primaryParticipant.lastName : formData.registrant.lastName,
          registrantEmail: registrationType === 'self' ? primaryParticipant.email : formData.registrant.email,
          registrantPhone: registrationType === 'self' ? primaryParticipant.phone : formData.registrant.phone,
          // All participants (for email body)
          participants: formData.participants,
          participantCount: formData.participants.length,
          // Organization details
            company: formData.company,
            website: formData.website,
            street: formData.street,
            houseNumber: formData.houseNumber,
            postalCode: formData.postalCode,
            city: formData.city,
          // Additional information
            notes: formData.notes,
            specialRequirements: formData.specialRequirements,
            knownFrom: formData.knownFrom,
            registrationDate: new Date().toISOString(),
          };
          
          try {
          console.log('Sending single email notification with payload:', emailPayload);
            const res = await fetch(functionUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(emailPayload),
            });
            
            if (!res.ok) {
            console.error('Failed to send notification email:', res.status, res.statusText);
              // Don't throw here - we don't want to fail the entire registration if email fails
            } else {
            console.log(`✅ Single notification email sent successfully for ${successfulRegistrations.length} participant(s)`);
            }
          } catch (emailError) {
          console.error('Error sending notification email:', emailError);
            // Don't throw here - we don't want to fail the entire registration if email fails
          }
      } else if (!functionUrl) {
          console.warn('No course registration email function URL configured - skipping email notification');
        }

      // Track successful course registration
      if (selectedCourse) {
        trackCourseRegistration(
          selectedCourse.title,
          selectedCourse.id,
          formData.participants.length
        );
      }

      // Show success screen instead of resetting form
      setShowSuccessScreen(true);
      setMessage(null); // Clear any existing messages
    } catch (err) {
      console.error('Error creating registration:', err);
      
      // Provide more specific error messages
      let errorMessage = 'Fehler bei der Anmeldung. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.';
      let errorType = 'Submission Error';
      
      if (err instanceof Error) {
        console.error('Error details:', err.message);
        if (err.message.includes('knownFrom')) {
          errorMessage = 'Fehler beim Speichern der "Bekannt aus" Information. Bitte versuchen Sie es ohne diese Angabe erneut.';
          errorType = 'Database Field Error';
        } else if (err.message.includes('Failed to create')) {
          errorMessage = 'Fehler beim Erstellen der Anmeldung. Bitte überprüfen Sie alle Angaben und versuchen Sie es erneut.';
          errorType = 'Registration Creation Failed';
        } else if (err.message.includes('Network')) {
          errorType = 'Network Error';
        } else if (err.message.includes('timeout')) {
          errorType = 'Timeout Error';
        }
      }
      
      // Track error in Matomo (GDPR-compliant, no PII)
      trackFormError(errorMessage, {
        formName: 'Course Registration',
        errorType,
        courseId: formData.courseId,
        participantCount: formData.participants.length,
        registrationType,
        step: currentStep
      });
      
      setMessage({ 
        type: 'error', 
        text: errorMessage
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      courseId: '',
      participants: [{ firstName: '', lastName: '', email: '', phone: '' }],
      registrant: { firstName: '', lastName: '', email: '', phone: '' },
      company: '',
      website: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      notes: '',
      specialRequirements: '',
      knownFrom: ''
    });
    setSelectedCourse(null);
    setMessage(null);
    setCurrentStep(1);
    setRegistrationType('self');
    setParticipantCount(1);
  };

  // Get step name for tracking
  const getStepName = (step: number): string => {
    const stepMap: { [key: number]: string } = {
      1: 'Registration Type',
      2: skipSelection ? 'Participant Details' : 'Course Selection',
      3: skipSelection ? 'Organization Details' : 'Participant Details',
      4: skipSelection ? 'Notes & Requirements' : 'Organization Details',
      5: skipSelection ? 'How Did You Hear About Us' : 'Notes & Requirements',
      6: 'How Did You Hear About Us'
    };
    return stepMap[step] || `Step ${step}`;
  };

  const handleNextStep = () => {
    if (validateCurrentStep()) {
      // Track step completion
      trackFormStep('Course Registration', currentStep, getStepName(currentStep), 'completed');
      trackStepTime('Course Registration', currentStep, Math.floor((Date.now() - stepStartTime) / 1000));
      
      // Track button click
      trackFormButton('Course Registration', 'Next', currentStep);
      
      setCurrentStep(prev => prev + 1);
      setStepStartTime(Date.now()); // Reset timer for next step
      setMessage(null);
    }
  };

  const handlePrevStep = () => {
    // Track going back
    trackFormStep('Course Registration', currentStep, getStepName(currentStep), 'back');
    trackFormButton('Course Registration', 'Back', currentStep);
    trackStepTime('Course Registration', currentStep, Math.floor((Date.now() - stepStartTime) / 1000));
    
    setCurrentStep(prev => Math.max(1, prev - 1));
    setStepStartTime(Date.now()); // Reset timer for previous step
    setMessage(null);
  };

  const validateRegistrant = (): boolean => {
    const registrant = formData.registrant;
    
    if (!registrant.firstName.trim()) {
      setMessage({ type: 'error', text: 'Bitte geben Sie Ihren Vornamen ein.' });
      return false;
    }
    if (!registrant.lastName.trim()) {
      setMessage({ type: 'error', text: 'Bitte geben Sie Ihren Nachnamen ein.' });
      return false;
    }
    if (!registrant.email.trim()) {
      setMessage({ type: 'error', text: 'Bitte geben Sie Ihre E-Mail-Adresse ein.' });
      return false;
    }
    if (!registrant.phone.trim()) {
      setMessage({ type: 'error', text: 'Bitte geben Sie Ihre Telefonnummer ein.' });
      return false;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(registrant.email)) {
      setMessage({ type: 'error', text: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.' });
      return false;
    }
    return true;
  };

  const validateParticipants = (): boolean => {
    // If registering others, validate registrant first
    if (registrationType === 'other' && !validateRegistrant()) {
      return false;
    }

    for (let i = 0; i < formData.participants.length; i++) {
      const participant = formData.participants[i];
      
      // Adjust error messages based on registration type
      const isSelfRegistration = registrationType === 'self';
      const personLabel = isSelfRegistration ? '' : ` für Person ${i + 1}`;
      
      if (!participant.firstName.trim()) {
        setMessage({ type: 'error', text: `Bitte geben Sie den Vornamen${personLabel} ein.` });
        return false;
      }
      if (!participant.lastName.trim()) {
        setMessage({ type: 'error', text: `Bitte geben Sie den Nachnamen${personLabel} ein.` });
        return false;
      }
      if (!participant.email.trim()) {
        setMessage({ type: 'error', text: `Bitte geben Sie die E-Mail-Adresse${personLabel} ein.` });
        return false;
      }
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(participant.email)) {
        setMessage({ type: 'error', text: `Bitte geben Sie eine gültige E-Mail-Adresse${personLabel} ein.` });
        return false;
      }
    }
    return true;
  };

  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1: // Registration type selection
        return true; // Always valid since we have a default
      case 2: // Course selection (if not skipped) OR Participant info (if skipped)
        if (skipSelection) {
          // When skipSelection is true, step 2 is participant information
          return validateParticipants();
        } else {
          // When skipSelection is false, step 2 is course selection
          if (!selectedCourse) {
            setMessage({ type: 'error', text: 'Bitte wählen Sie einen Kurs aus.' });
            return false;
          }
          return true;
        }
      case 3: // Participant info (if not skipped) OR Organization info (if skipped)
        if (skipSelection) {
          // When skipSelection is true, step 3 is organization information
          return true; // No required fields in organization step
        } else {
          // When skipSelection is false, step 3 is participant information
          return validateParticipants();
        }
      case 4: // Organization info (if not skipped) OR Notes (if skipped)
        if (skipSelection) {
          // When skipSelection is true, step 4 is notes
          return true; // No required fields in notes step
        } else {
          // When skipSelection is false, step 4 is organization information
          return true; // No required fields in organization step
        }
      case 5: // Notes step (if not skipped) OR Known From step (if skipped)
        if (skipSelection) {
          // When skipSelection is true, step 5 is Known From step
          return true; // No required fields in Known From step
        } else {
          // When skipSelection is false, step 5 is notes step
          return true; // No required fields in notes step
        }
      case 6: // Known From step (final form step, only when not skipSelection)
        // No required fields in Known From step
        return true;
      default:
        return true;
    }
  };

  const getMaxStep = (): number => {
    if (skipSelection) {
      return 5; // Registration type -> Basic info -> Organization info -> Notes -> Known From
    }
    return 6; // Registration type -> Course selection -> Basic info -> Organization info -> Notes -> Known From
  };

  if (loading) {
    return (
      <div className="registration-form-container">
        <div className="loading-state" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
          <div className="loading-spinner"></div>
          <p style={{ textAlign: 'center', margin: '0 auto', width: '100%' }}>{skipSelection ? 'Anmeldeformular wird geladen...' : 'Kurse werden geladen...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-form-container" data-section="registration-form" data-page={skipSelection ? 'course-detail' : 'anmeldung'}>
      <div className="registration-header">
        <h2 className="course-section-header">Kurs-Anmeldung</h2>
        {!skipSelection && (
          <p>Wählen Sie einen Kurs aus und füllen Sie das Anmeldeformular aus.</p>
        )}
      </div>

      {/* Step Progress Indicator - Only show when not in success screen */}
      {!showSuccessScreen && (
        <div className="step-progress">
          <div className="step-progress-bar">
            {Array.from({ length: getMaxStep() }, (_, index) => (
              <div
                key={index + 1}
                className={`step-dot ${currentStep >= index + 1 ? 'active' : ''} ${currentStep === index + 1 ? 'current' : ''}`}
              />
            ))}
          </div>
          <p className="step-text">Schritt {currentStep} von {getMaxStep()}</p>
        </div>
      )}

      {/* Registration Type Selection - Step 1 */}
      {!showSuccessScreen && currentStep === 1 && (
        <div className="registration-type-section">
          <h3>Für wen melden Sie an?</h3>
          <div className="registration-type-options">
            <div 
              className={`registration-type-card ${registrationType === 'self' ? 'selected' : ''}`}
              onClick={() => setRegistrationType('self')}
            >
              <h4>Für mich selbst</h4>
              <p>Ich melde mich persönlich für den Kurs an</p>
            </div>
            
            <div 
              className={`registration-type-card ${registrationType === 'other' ? 'selected' : ''}`}
              onClick={() => setRegistrationType('other')}
            >
              <h4>Für eine andere Person</h4>
              <p>Ich melde eine andere Person für den Kurs an</p>
            </div>
          </div>
          
          {/* Participant Count Selection - Only show when registering for others */}
          {registrationType === 'other' && (
            <div className="participant-count-section">
              <h4>Anzahl der Teilnehmer</h4>
              <p>Für wie viele Personen möchten Sie eine Anmeldung durchführen?</p>
              <div className="participant-count-options">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((count) => (
                  <button
                    key={count}
                    type="button"
                    className={`participant-count-btn ${participantCount === count ? 'selected' : ''}`}
                    onClick={() => setParticipantCount(count)}
                  >
                    {count}
                  </button>
                ))}
                <div className="custom-count">
                  <label htmlFor="customCount">Andere Anzahl:</label>
                  <input
                    type="number"
                    id="customCount"
                    min="1"
                    max="50"
                    value={participantCount > 10 ? participantCount : ''}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 1;
                      if (value >= 1 && value <= 50) {
                        setParticipantCount(value);
                      }
                    }}
                    placeholder="11+"
                  />
                </div>
              </div>
            </div>
          )}
          
          <div className="step-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleNextStep}
            >
              Weiter
            </button>
          </div>
        </div>
      )}

      {/* Course Selection - Step 2 (only if not skipped) */}
      {!showSuccessScreen && !skipSelection && currentStep === 2 && (
        <div className="course-selection-section">
          <h3>Kurs auswählen</h3>
          {courses.length === 0 ? (
            <div className="no-courses">
              <p>Aktuell sind keine Kurse verfügbar. Schauen Sie bald wieder vorbei!</p>
            </div>
          ) : (
            <div className="course-cards-grid">
              {courses.map((course) => (
                <div
                  key={course.id}
                  className={`course-card ${selectedCourse?.id === course.id ? 'selected' : ''}`}
                  onClick={() => handleCourseSelect(course)}
                >
                  <div className="course-card-header">
                    <h4>{course.title}</h4>
                    <div className="course-price">{course.price}</div>
                  </div>
                  
                  <div className="course-card-content">
                    <p className="course-description">{course.description}</p>
                    
                    <div className="course-details">
                      <div className="detail-item">
                        <span className="detail-icon">👨‍🏫</span>
                        <span>Dozent: {course.instructor}</span>
                      </div>
                      
                      <div className="detail-item">
                        <span className="detail-icon">⏱️</span>
                        <span>Dauer: {course.duration}</span>
                      </div>
                      
                      <div className="detail-item">
                        <span className="detail-icon">📍</span>
                        <span>Ort: {course.location || 'Wird bekannt gegeben'}</span>
                      </div>
                      
                      {course.startDate && (
                        <div className="detail-item">
                          <span className="detail-icon">📅</span>
                          <span>Beginn: {course.startDate}</span>
                        </div>
                      )}
                      
                      <div className="detail-item">
                        <span className="detail-icon">🎯</span>
                        <span>Level: {course.level}</span>
                      </div>
                      
                      {course.targetgroup && (
                        <div className="detail-item">
                          <span className="detail-icon">👥</span>
                          <span>Zielgruppe: {course.targetgroup}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="course-card-footer">
                    {selectedCourse?.id === course.id ? (
                      <span className="selected-indicator">✅ Ausgewählt</span>
                    ) : (
                      <span className="select-hint">Klicken zum Auswählen</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="step-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handlePrevStep}
            >
              Zurück
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleNextStep}
              disabled={!selectedCourse}
            >
              Weiter
            </button>
          </div>
        </div>
      )}

      {/* Participant Information - Step 3 (or Step 2 if skipSelection) */}
      {!showSuccessScreen && ((skipSelection && currentStep === 2) || (!skipSelection && currentStep === 3)) && (
        <div className="registration-form-section">
          <h3>
            {registrationType === 'self' 
              ? 'Angaben zur Person' 
              : 'Angaben zu Ihnen'
            }
          </h3>
          
          <div className="registration-form">
            <div className="honeypot-fields" aria-hidden="true">
              <label htmlFor="hp_website">Website</label>
              <input type="text" id="hp_website" name="hp_website" value={formData.hp_website} onChange={handleInputChange} autoComplete="off" tabIndex={-1} />
              <label htmlFor="hp_confirm_email">Confirm Email</label>
              <input type="text" id="hp_confirm_email" name="hp_confirm_email" value={formData.hp_confirm_email} onChange={handleInputChange} autoComplete="off" tabIndex={-1} />
            </div>
            {registrationType === 'self' ? (
              // Simple form for self-registration
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="firstName">Vorname *</label>
                  <input
                    type="text"
                    id="firstName"
                    value={formData.participants[0]?.firstName || ''}
                    onChange={(e) => handleParticipantChange(0, 'firstName', e.target.value)}
                    required
                    placeholder="Ihr Vorname"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Nachname *</label>
                  <input
                    type="text"
                    id="lastName"
                    value={formData.participants[0]?.lastName || ''}
                    onChange={(e) => handleParticipantChange(0, 'lastName', e.target.value)}
                    required
                    placeholder="Ihr Nachname"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">E-Mail *</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.participants[0]?.email || ''}
                    onChange={(e) => handleParticipantChange(0, 'email', e.target.value)}
                    required
                    placeholder="ihre.email@beispiel.de"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Telefon</label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.participants[0]?.phone || ''}
                    onChange={(e) => handleParticipantChange(0, 'phone', e.target.value)}
                    placeholder="+49 123 456789"
                  />
                </div>
              </div>
            ) : (
              // Multi-participant form for registering others
              <div>
                {/* Registrant Information Section */}
                <div className="form-grid registrant-section">
                  <div className="form-group">
                    <label htmlFor="registrant-firstName">Ihr Vorname *</label>
                    <input
                      type="text"
                      id="registrant-firstName"
                      value={formData.registrant.firstName}
                      onChange={(e) => handleRegistrantChange('firstName', e.target.value)}
                      required
                      placeholder="Ihr Vorname"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="registrant-lastName">Ihr Nachname *</label>
                    <input
                      type="text"
                      id="registrant-lastName"
                      value={formData.registrant.lastName}
                      onChange={(e) => handleRegistrantChange('lastName', e.target.value)}
                      required
                      placeholder="Ihr Nachname"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="registrant-email">Ihre E-Mail *</label>
                    <input
                      type="email"
                      id="registrant-email"
                      value={formData.registrant.email}
                      onChange={(e) => handleRegistrantChange('email', e.target.value)}
                      required
                      placeholder="ihre.email@beispiel.de"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="registrant-phone">Ihr Telefon *</label>
                    <input
                      type="tel"
                      id="registrant-phone"
                      value={formData.registrant.phone}
                      onChange={(e) => handleRegistrantChange('phone', e.target.value)}
                      required
                      placeholder="+49 123 456789"
                    />
                  </div>
                </div>

                {/* Participants Information Section */}
                <div className="participants-section">
                  <h4>{`Angaben für ${participantCount} ${participantCount === 1 ? 'Person' : 'Personen'}`}</h4>
                  <div className="participants-grid">
                    {formData.participants.map((participant, index) => (
                      <div key={index} className="participant-row">
                        <div className="participant-number">
                          <span>Person {index + 1}</span>
                        </div>
                        <div className="participant-fields">
                          <div className="form-group">
                            <input
                              type="text"
                              id={`firstName-${index}`}
                              value={participant.firstName}
                              onChange={(e) => handleParticipantChange(index, 'firstName', e.target.value)}
                              required
                              placeholder="Vorname"
                            />
                          </div>

                          <div className="form-group">
                            <input
                              type="text"
                              id={`lastName-${index}`}
                              value={participant.lastName}
                              onChange={(e) => handleParticipantChange(index, 'lastName', e.target.value)}
                              required
                              placeholder="Nachname"
                            />
                          </div>

                          <div className="form-group">
                            <input
                              type="email"
                              id={`email-${index}`}
                              value={participant.email}
                              onChange={(e) => handleParticipantChange(index, 'email', e.target.value)}
                              required
                              placeholder="E-Mail"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handlePrevStep}
                disabled={submitting}
              >
                Zurück
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNextStep}
              >
                Weiter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Organization Information - Step 4 (or Step 3 if skipSelection) */}
      {!showSuccessScreen && ((skipSelection && currentStep === 3) || (!skipSelection && currentStep === 4)) && (
        <div className="registration-form-section">
          <h3>Angaben zur Organisation</h3>
          
          <div className="registration-form">
            <div className="honeypot-fields" aria-hidden="true">
              <label htmlFor="hp_website">Website</label>
              <input type="text" id="hp_website" name="hp_website" value={formData.hp_website} onChange={handleInputChange} autoComplete="off" tabIndex={-1} />
              <label htmlFor="hp_confirm_email">Confirm Email</label>
              <input type="text" id="hp_confirm_email" name="hp_confirm_email" value={formData.hp_confirm_email} onChange={handleInputChange} autoComplete="off" tabIndex={-1} />
            </div>
            <div className="form-grid">
              <div className="form-group full-width">
                <label htmlFor="company">Unternehmen/Organisation</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder={registrationType === 'self' ? "Ihr Unternehmen" : "Unternehmen der Person"}
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="website">Website</label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://www.beispiel.de"
                />
              </div>

              <div className="form-group">
                <label htmlFor="street">Straße</label>
                <input
                  type="text"
                  id="street"
                  name="street"
                  value={formData.street}
                  onChange={handleInputChange}
                  placeholder="Musterstraße"
                />
              </div>

              <div className="form-group">
                <label htmlFor="houseNumber">Hausnummer</label>
                <input
                  type="text"
                  id="houseNumber"
                  name="houseNumber"
                  value={formData.houseNumber}
                  onChange={handleInputChange}
                  placeholder="123a"
                />
              </div>

              <div className="form-group">
                <label htmlFor="postalCode">PLZ</label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  placeholder="12345"
                  pattern="[0-9]{5}"
                  maxLength={5}
                />
              </div>

              <div className="form-group">
                <label htmlFor="city">Ort</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Musterstadt"
                />
              </div>



            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handlePrevStep}
                disabled={submitting}
              >
                Zurück
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNextStep}
              >
                Weiter
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Notes and Special Requirements - Step 5 (or Step 4 if skipSelection) */}
      {!showSuccessScreen && ((skipSelection && currentStep === 4) || (!skipSelection && currentStep === 5)) && (
        <div className="registration-form-section">
          <h3>Anmerkungen und besondere Anforderungen</h3>
          
          <div className="registration-form">
            <div className="honeypot-fields" aria-hidden="true">
              <label htmlFor="hp_website">Website</label>
              <input type="text" id="hp_website" name="hp_website" value={formData.hp_website} onChange={handleInputChange} autoComplete="off" tabIndex={-1} />
              <label htmlFor="hp_confirm_email">Confirm Email</label>
              <input type="text" id="hp_confirm_email" name="hp_confirm_email" value={formData.hp_confirm_email} onChange={handleInputChange} autoComplete="off" tabIndex={-1} />
            </div>
            <div className="form-grid">
              <div className="form-group full-width">
                <label htmlFor="notes">Anmerkungen</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Besondere Wünsche oder Fragen..."
                  rows={3}
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="specialRequirements">Besondere Anforderungen</label>
                <textarea
                  id="specialRequirements"
                  name="specialRequirements"
                  value={formData.specialRequirements}
                  onChange={handleInputChange}
                  placeholder="z.B. Barrierefreiheit, Diätwünsche..."
                  rows={2}
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handlePrevStep}
                disabled={submitting}
              >
                Zurück
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNextStep}
                disabled={submitting}
              >
                Weiter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Known From - Step 6 (or Step 5 if skipSelection) */}
      {!showSuccessScreen && ((skipSelection && currentStep === 5) || (!skipSelection && currentStep === 6)) && (
        <form onSubmit={handleSubmit} className="registration-form-section">
          <h3>Wie haben Sie von uns erfahren?</h3>
          
          <div className="registration-form">
            <div className="honeypot-fields" aria-hidden="true">
              <label htmlFor="hp_website">Website</label>
              <input type="text" id="hp_website" name="hp_website" value={formData.hp_website} onChange={handleInputChange} autoComplete="off" tabIndex={-1} />
              <label htmlFor="hp_confirm_email">Confirm Email</label>
              <input type="text" id="hp_confirm_email" name="hp_confirm_email" value={formData.hp_confirm_email} onChange={handleInputChange} autoComplete="off" tabIndex={-1} />
            </div>
            
            <div className="form-grid">
              <div className="form-group full-width">
                <p className="known-from-description">
                  Helfen Sie uns dabei, unsere Kurse noch besser zu bewerben! 
                  Teilen Sie uns mit, wie Sie auf uns und diesen Kurs aufmerksam geworden sind.
                </p>
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="knownFrom">Quelle (optional)</label>
                <select
                  id="knownFrom"
                  name="knownFrom"
                  value={formData.knownFrom}
                  onChange={handleSelectChange}
                  className="form-select"
                >
                  <option value="">Bitte wählen...</option>
                  <option value="google">Google Suche</option>
                  <option value="social-media">Social Media (LinkedIn, Facebook, etc.)</option>
                  <option value="recommendation">Empfehlung von Kollegen/Freunden</option>
                  <option value="website">Direkt über unsere Website</option>
                  <option value="newsletter">Newsletter/E-Mail Marketing</option>
                  <option value="event">Veranstaltung/Messe</option>
                  <option value="partner">Partner/Referenz</option>
                  <option value="other">Sonstiges</option>
                </select>
              </div>
              
              {formData.knownFrom === 'other' && (
                <div className="form-group full-width">
                  <label htmlFor="knownFromOther">Bitte spezifizieren</label>
                  <input
                    type="text"
                    id="knownFromOther"
                    name="knownFromOther"
                    placeholder="Wie haben Sie von uns erfahren?"
                    className="form-input"
                  />
                </div>
              )}
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handlePrevStep}
                disabled={submitting}
              >
                Zurück
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={submitting}
                onClick={() => {
                  // Update knownFrom if "other" was selected with custom text
                  if (formData.knownFrom === 'other') {
                    const otherInput = document.getElementById('knownFromOther') as HTMLInputElement;
                    if (otherInput && otherInput.value.trim()) {
                      setFormData(prev => ({
                        ...prev,
                        knownFrom: `Sonstiges: ${otherInput.value.trim()}`
                      }));
                    }
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {submitting && (
                  <svg 
                    className="spinner" 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      animation: 'spin 1s linear infinite'
                    }}
                  >
                    <circle 
                      cx="12" 
                      cy="12" 
                      r="10" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      fill="none" 
                      strokeLinecap="round"
                      style={{
                        opacity: 0.25
                      }}
                    />
                    <path 
                      d="M12 2 A10 10 0 0 1 22 12" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      fill="none" 
                      strokeLinecap="round"
                    />
                  </svg>
                )}
                {submitting ? 'Wird gesendet...' : 'Anmeldung absenden'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Success Screen - Shows after form submission */}
      {showSuccessScreen && (
        <div className="registration-form-section success-section">
          <div className="success-content">
            <div className="success-icon"></div>
            <h3>Anmeldung erfolgreich!</h3>
            <p className="success-message">
              Vielen Dank für Ihre Anmeldung! Wir haben Ihre Registrierung erhalten und werden uns 
              in Kürze mit weiteren Informationen bei Ihnen melden.
            </p>
            
            <div className="next-steps">
              <h4>Was passiert als nächstes?</h4>
              <ul>
                <li>Sie erhalten eine Bestätigungs-E-Mail mit allen Details</li>
                <li>Wir prüfen Ihre Anmeldung und senden Ihnen die Rechnung</li>
                <li>Kurz vor dem Kurs erhalten Sie alle wichtigen Informationen</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {!showSuccessScreen && !skipSelection && courses.length > 0 && !selectedCourse && (
        <div className="selection-hint">
          <p>👆 Bitte wählen Sie zuerst einen Kurs aus, um das Anmeldeformular zu öffnen.</p>
        </div>
      )}

      {!showSuccessScreen && message && (
        <StatusMessage
          type={message.type}
          message={message.text}
          onClose={() => setMessage(null)}
        />
      )}
    </div>
  );
};

export default CourseRegistrationForm;
